import { T, always, cond, equals } from 'ramda';
import React, { FC, useEffect } from 'react';
import ReactCanvasConfetti from 'react-canvas-confetti';

import Button, { IButtonProps } from '@ui/atoms/Button';
import FlexColumn, { IFlexColumn } from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';
import WYSIWYGContent from '@ui/atoms/WYSIWYGContent';

import aligned from '@ui/styled/Aligned';
import fullWidth from '@ui/styled/FullWidth';
import spaced from '@ui/styled/Spaced';

import useConfetti from '@hooks/useConfetti';
import useWindowSize from '@hooks/useWindowSize';

import confetti from '@utils/confetti';

import { IFlowStepComponentProps } from '@folx-types/flow-steps';

export interface INextStepsStepPageContent {
	pageHeader: string;
	pageDescription: string;
	getCareLabel: string;
	getCareHref: string;
}

interface INextStepsStepProps extends IFlowStepComponentProps {
	pageContent: INextStepsStepPageContent;
}

const FullWidthColumn = aligned<IFlexColumn>(
	spaced<IFlexColumn>(fullWidth<IFlexColumn>(FlexColumn))
);
const FullWidthButton = fullWidth<IButtonProps>(Button);

const NextStepsStep: FC<INextStepsStepProps> = ({ pageContent }) => {
	const { pageHeader, pageDescription, getCareLabel, getCareHref } = pageContent;

	// Confetti Configuration
	const { windowSize } = useWindowSize();
	const multiplier = cond([
		[equals('mobile'), always(1)],
		[equals('tablet'), always(2)],
		[equals('laptop'), always(3)],
		[equals('desktop'), always(4)],
		[T, always(1)],
	])(windowSize);

	const originY = cond([
		[equals('mobile'), always(0.3)],
		[equals('tablet'), always(0.4)],
		[equals('laptop'), always(0.5)],
		[equals('desktop'), always(0.6)],
	])(windowSize);

	const defaultStartVelocity = 25;
	const confettiProps = { startVelocity: defaultStartVelocity * multiplier };

	const left = confetti(confettiProps).membershipSpread.left;
	const right = confetti(confettiProps).membershipSpread.right;

	left.origin.y = originY;
	right.origin.y = originY;

	const defaultInterval = 40;
	const interval = defaultInterval / multiplier;

	const settings = [left, right];
	const { getInstance, defaultCanvasStyle, startAnimation, pauseAnimation } = useConfetti(
		settings,
		interval
	);

	useEffect(() => {
		startAnimation();
		setTimeout(() => {
			pauseAnimation();
		}, 4000);
	}, []);

	return (
		<>
			<ReactCanvasConfetti refConfetti={getInstance} style={defaultCanvasStyle} />
			<FullWidthColumn alignItems="flex-start" gap="sm" marginTop="xxxxl" marginBottom="xxl">
				<HeadingText fontSize="md" fontWeight="normal">
					{pageHeader}
				</HeadingText>
				<WYSIWYGContent
					dangerouslySetInnerHTML={{
						__html: pageDescription,
					}}
				/>
			</FullWidthColumn>
			<FullWidthColumn alignItems="center" gap="sm" marginBottom="xxxxl">
				<FullWidthButton label={getCareLabel} href={getCareHref} variant="secondary" />
			</FullWidthColumn>
		</>
	);
};

export default NextStepsStep;
